import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { ToolbarNoteListItem } from '#/shared/api/generated/shipment/models'
import {
  useCreateItemApiV1ToolbarPost,
  useListItemsApiV1ToolbarTypeGet,
} from '#/shared/api/generated/shipment/theCargoShipment'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { formatNoteTimestamp } from '#/shared/lib/format'
import { useSingleOpen } from '#/shared/lib/hooks'
import { toast } from '#/shared/ui/sonner'
import type { NotePreviewItem } from '#/shared/ui/table-cells'

const NOTE_PREVIEW_SIZE = 100
const TOOLBAR_NOTE_KEY_PREFIX = '/api/v1/toolbar/note'

function formatAuthor(user: ToolbarNoteListItem['user']): string {
  if (!user) return ''
  return [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
}

function toPreview(note: ToolbarNoteListItem): NotePreviewItem {
  return {
    text: note.text,
    date: formatNoteTimestamp(note.created_at),
    author: formatAuthor(note.user),
  }
}

export interface UseShipmentNotesResult {
  notes: NotePreviewItem[]
  isLoading: boolean
  isSaving: boolean
  isEditing: boolean
  setEditing: (open: boolean) => void
  arm: () => void
  addNote: (text: string) => void
}

export function useShipmentNotes(
  shipmentId: string,
  count: number | undefined,
): UseShipmentNotesResult {
  const { t } = useTranslation()
  const [armed, setArmed] = useState(false)
  const hasNotes = (count ?? 0) > 0
  const editor = useSingleOpen(shipmentId)
  const queryClient = useQueryClient()

  const query = useListItemsApiV1ToolbarTypeGet(
    'note',
    { shipment_id: shipmentId, size: NOTE_PREVIEW_SIZE },
    { query: { enabled: armed && hasNotes } },
  )

  const createNote = useCreateItemApiV1ToolbarPost({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [TOOLBAR_NOTE_KEY_PREFIX] })
        queryClient.invalidateQueries({ queryKey: ['/api/v1/shipments'] })
        editor.close()
        toast.success(t('table.note.addSuccess'))
      },
      onError: (error) => {
        toast.error(getApiErrorDetail(error) ?? t('table.note.addError'))
      },
    },
  })

  const notes = useMemo<NotePreviewItem[]>(() => {
    const items = (query.data?.data.items ??
      []) as unknown as ToolbarNoteListItem[]
    return items.map(toPreview)
  }, [query.data])

  return {
    notes,
    isLoading: hasNotes && query.isLoading,
    isSaving: createNote.isPending,
    isEditing: editor.isOpen,
    setEditing: (open) => (open ? editor.open() : editor.close()),
    arm: () => setArmed(true),
    addNote: (text) =>
      createNote.mutate({
        data: { kind: 'note', shipment_id: shipmentId, text },
      }),
  }
}
